// Bouton micro pour activer la dictée Web Speech API à côté d'un textarea.
// Toggle start/stop. Au stop, merge le transcript dicté avec la valeur courante
// du textarea (callback `onTranscript(merged)`).

import { useEffect, useRef } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import useSpeechRecognition from "../../hooks/useSpeechRecognition";
import { accumulerDictee, finaliserDictee } from "./speechHelpers";

export default function MicButton({ value, onTranscript, lang = "fr-FR" }) {
  const { supported, listening, transcript, interimTranscript, error, start, stop } =
    useSpeechRecognition({ lang });
  // 🔴 LA DICTÉE EST UNE BASE + UN TRANSCRIPT, ⛔ PAS UNE SUITE D'AJOUTS.
  //
  // ⚠️ L'OBJECTIF DU « fix dictée dupliquée (2026-05-20) » EST CONSERVÉ — il
  // est même rendu STRUCTUREL. L'ancien montage poussait un DELTA à chaque
  // chunk : il fallait un compteur d'offset pour ne pas ré-envoyer ce qui
  // l'avait déjà été, et cet offset se décalait dès que le moteur révisait un
  // résultat (coupe au milieu d'un mot). Ici on ⛔ n'AJOUTE jamais : on RECALCULE
  // `base + transcript` à chaque fois. Envoyer deux fois le même transcript
  // donne le même texte ⇒ la duplication est IMPOSSIBLE, ⛔ pas seulement
  // évitée — et il n'y a plus d'arithmétique de position à désynchroniser.
  //
  // 🔑 BASE = ce que contenait le champ QUAND LE MICRO A DÉMARRÉ. On la fige,
  // sinon elle inclurait la dictée en cours et le texte se recopierait.
  const baseRef = useRef("");
  const dictaitRef = useRef(false);

  useEffect(() => {
    if (listening && !dictaitRef.current) {
      dictaitRef.current = true;
      baseRef.current = value || "";
    }
    // 🔴 LA MISE EN FORME A LIEU ICI, ET NULLE PART AILLEURS : à l'arrêt du
    // micro, une fois. ⛔ Pas à chaque chunk — un chunk est une respiration,
    // ⛔ pas une phrase, et le ponctuer fabrique des prestations (P0-2).
    if (!listening && dictaitRef.current) {
      dictaitRef.current = false;
      const brut = accumulerDictee(baseRef.current, transcript);
      if (brut) onTranscript(finaliserDictee(brut));
    }
  }, [listening]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!dictaitRef.current) return;
    // Pendant la dictée : texte BRUT, collé, tel qu'il est prononcé.
    onTranscript(accumulerDictee(baseRef.current, transcript));
  }, [transcript]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🔴 ⛔ PLUS DE `return null`. UNE FONCTION ESSENTIELLE ⛔ NE DISPARAÎT PAS.
  //
  // ⚠️ CE QUE FAISAIT L'ANCIEN CODE : quand le navigateur ⛔ n'expose PAS la
  // reconnaissance vocale, le bouton ⛔ n'était PAS rendu — DU TOUT. L'écran
  // était donc NORMAL, simplement sans micro, et ⛔ RIEN ⛔ ne le disait.
  // 🔑 Le cas nous vise directement : sur iOS, `webkitSpeechRecognition` peut
  // être absente en mode PWA INSTALLÉE alors qu'elle est là dans Safari. Mario
  // aurait ouvert son icône, ⛔ n'aurait pas vu le bouton, et aurait conclu que
  // « ça ne marche pas » — sans que personne ⛔ ne sache pourquoi.
  // ⇒ ÉTAT EXPLICITE : on rend un bouton DÉSACTIVÉ qui NOMME l'indisponibilité
  // et dit le contournement. ⛔ Le comportement quand c'est supporté ⛔ ne change
  // PAS d'un pixel — seule la branche « non supporté » est concernée.
  if (!supported) {
    return (
      <span
        role="status"
        data-testid="dal-mic-indisponible"
        title="Dictée indisponible dans ce contexte. Ouvrez la page dans Safari, ou saisissez le texte."
        className="flex items-center gap-1 text-xs px-2 py-1 rounded"
        style={{ background: "var(--oh-stat-amber-bg)",
                 color: "var(--oh-stat-amber-text)" }}
      >
        <MicOff className="w-3 h-3" />
        Dictée indisponible ici
      </span>
    );
  }

  const handleToggle = () => {
    if (listening) stop();
    else start();
  };

  return (
    <button
      type="button"
      onClick={handleToggle}
      title={listening ? "Arrêter la dictée" : "Dicter"}
      data-testid="dal-mic-btn"
      className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
        listening
          ? "bg-[var(--oh-error-bg)] text-[var(--oh-error-text)] ring-1 ring-red-500/50 animate-pulse"
          : "bg-[var(--oh-bg-elevated)] text-[var(--oh-text-secondary)] hover:bg-[var(--oh-bg-surface)]"
      }`}
    >
      {error ? <MicOff className="w-3 h-3" /> : listening ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mic className="w-3 h-3" />}
      {listening ? "Stop" : "Dicter"}
      {listening && interimTranscript && (
        <span className="text-[9px] italic max-w-[120px] truncate">{interimTranscript}</span>
      )}
      {/* 🔴 L'ERREUR EST DITE, ⛔ pas réduite à une icône barrée. Un micro
          refusé (permission) et un micro absent ⛔ ne se corrigent PAS pareil :
          l'un se ré-autorise, l'autre impose de changer de contexte. */}
      {error && !listening && (
        <span className="text-[9px] max-w-[140px] truncate"
              data-testid="dal-mic-erreur"
              style={{ color: "var(--oh-stat-red-text)" }}>
          micro refusé — vérifiez l'autorisation
        </span>
      )}
    </button>
  );
}
